import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUserRole } from '@/hooks/use-user-role';

interface Student {
  id: string;
  full_name: string;
  grade: string | null;
}

interface SelectedStudentContextType {
  students: Student[];
  selectedStudent: Student | null;
  setSelectedStudent: (student: Student | null) => void;
  isLoading: boolean;
}

const SelectedStudentContext = createContext<SelectedStudentContextType | undefined>(undefined);

export const SelectedStudentProvider = ({ children }: { children: ReactNode }) => {
  const [selectedStudent, setSelectedStudentState] = useState<Student | null>(null);
  const { userId: currentUserId, roles } = useUserRole();
  const isParent = roles.includes('parent');

  const { data: students = [], isLoading } = useQuery({
    queryKey: ['parent-students-list', currentUserId],
    queryFn: async () => {
      if (!currentUserId) return [];

      const { data, error } = await supabase
        .from('student_profiles')
        .select('id, full_name, grade')
        .eq('parent_user_id', currentUserId)
        .eq('archived', false)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    // Role guard: this context is parent-only. Skip the query entirely for
    // admins, centre managers, tutors, and partners to save a round-trip
    // on every session.
    enabled: !!currentUserId && isParent,
    staleTime: 2 * 60 * 1000,
  });

  // Auto-select first student when students load
  useEffect(() => {
    if (students.length > 0 && !selectedStudent) {
      // Check localStorage for previously selected student
      const savedStudentId = localStorage.getItem('selectedStudentId');
      const savedStudent = students.find(s => s.id === savedStudentId);
      setSelectedStudentState(savedStudent || students[0]);
    }
  }, [students, selectedStudent]);

  const setSelectedStudent = (student: Student | null) => {
    setSelectedStudentState(student);
    if (student) {
      localStorage.setItem('selectedStudentId', student.id);
    } else {
      localStorage.removeItem('selectedStudentId');
    }
  };

  return (
    <SelectedStudentContext.Provider value={{ 
      students, 
      selectedStudent, 
      setSelectedStudent,
      isLoading 
    }}>
      {children}
    </SelectedStudentContext.Provider>
  );
};

export const useSelectedStudent = () => {
  const context = useContext(SelectedStudentContext);
  if (context === undefined) {
    throw new Error('useSelectedStudent must be used within a SelectedStudentProvider');
  }
  return context;
};
